import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import runes from "@-/com.npmjs.runes2";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { cn } from "@-/com.npmjs.cn";
import { useScopedBack } from "@-/dev.improve.haniverse.routes.nav._lib.back";
import { View } from "@-/com.npmjs.react-native";
import { hasMinLength } from "@-/com.npmjs.ts-array-length";
import { useMorphemeRouteBodyReady } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels.route-body";

type MorphemeNotFoundKind = "character" | "word" | "primitive";

function getMorphemeNotFoundKind(morpheme: string): Exclude<MorphemeNotFoundKind, "primitive"> {
  if (hasMinLength(runes(morpheme), 2)) {
    return "word";
  }
  // Primitive classification is not implemented yet — single graphemes are characters.
  return "character";
}

function getMorphemeNotFoundTitle(kind: MorphemeNotFoundKind): string {
  switch (kind) {
    case "word": {
      return "Word not found";
    }
    case "character": {
      return "Character not found";
    }
    case "primitive": {
      return "Primitive not found";
    }
  }
}

function getMorphemeNotFoundDescription(kind: MorphemeNotFoundKind): string {
  switch (kind) {
    case "word": {
      return "No morpheme exists for this word. It may not be in your study or the content database yet.";
    }
    case "character": {
      return "No morpheme exists for this character. It may not be in your study or the content database yet.";
    }
    case "primitive": {
      return "No morpheme exists for this primitive. It may not be in your study or the content database yet.";
    }
  }
}

export function MorphemeNotFound({
  className,
  description,
  morpheme,
}: {
  className?: string;
  description?: string;
  morpheme: string;
}) {
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const graphemeGlyph = getGraphemeGlyph(morpheme);
  const { canGoBack, goBack } = useScopedBack();
  const kind = getMorphemeNotFoundKind(morpheme);
  const title = getMorphemeNotFoundTitle(kind);
  const resolvedDescription = description ?? getMorphemeNotFoundDescription(kind);
  const onBodyReady = useMorphemeRouteBodyReady();

  return (
    <View
      className={cn("flex-1 items-center justify-center gap-4 px-6", className)}
      onLayout={(event) => {
        if (event.nativeEvent.layout.width > 0 && event.nativeEvent.layout.height > 0) {
          onBodyReady?.();
        }
      }}
    >
      <GraphemeFontGlyph
        className="text-7xl leading-none text-muted-foreground/70"
        glyph={graphemeGlyph}
      />
      <View className="max-w-sm items-center gap-2">
        <Text
          accessibilityLabel={`${title}: ${morpheme}`}
          className="text-center text-xl font-semibold text-foreground"
          selectable
        >
          {title}: &ldquo;{morpheme}&rdquo;
        </Text>
        <Text className="text-center text-base text-muted-foreground">{resolvedDescription}</Text>
      </View>
      {canGoBack ? (
        <Button className="mt-2" onPress={() => goBack()} variant="outline">
          <Text>Go back</Text>
        </Button>
      ) : null}
    </View>
  );
}
